import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { submitContact, getSettings } from '@/lib/api';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings('contact');
        setContactSettings(response.data?.value || {});
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const rawContact = contactSettings || {
    title: 'Bizimle İletişime Geçin',
    subtitle: 'Sorularınız, önerileriniz veya projeleriniz için bizimle iletişime geçebilirsiniz. Size en kısa sürede dönüş yapacağız.',
    address: 'Kozyatağı Mahallesi İbrahim Ağa Sokak No:12 Kat:6 D:23',
    addressDetail: 'Çelikbilek Plaza, Kadıköy, İstanbul',
    phone: '0216 606 80 68',
    phoneHours: 'Pazartesi - Cuma: 09:00 - 18:00',
    email: 'info@etaglobalgroup.com',
    emailNote: 'Sorularınız için bize yazın',
    workingHours: 'Pzt - Cum: 09:00 - 18:00',
    workingHoursNote: 'Cumartesi - Pazar: Kapalı'
  };

  // Force latest contact details even if DB holds legacy values
  const contact = {
    ...rawContact,
    phone: '0216 606 80 68',
    email: 'info@etaglobalgroup.com'
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitContact(formData);
      setSubmitted(true);
      toast.success('Mesajınız başarıyla gönderildi!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adres',
      content: contact.address,
      subContent: contact.addressDetail
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: contact.phone,
      subContent: contact.phoneHours
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: contact.email,
      subContent: contact.emailNote
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: contact.workingHours,
      subContent: contact.workingHoursNote
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-slate-50 py-20" data-testid="contact-hero">
        <div className="eta-container">
          <div className="max-w-3xl">
            <p className="eta-caption text-[#005CB2] mb-4">İLETİŞİM</p>
            <h1 className="eta-heading-1 text-slate-900 mb-6">
              {contact.title}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              {contact.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="eta-section bg-white" data-testid="contact-section">
        <div className="eta-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <h2 className="eta-heading-3 mb-6">İletişim Bilgileri</h2>
              
              {contactInfo.map((info, index) => (
                <Card 
                  key={index} 
                  data-testid={`contact-info-${index}`}
                  className="border-slate-200"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#005CB2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-[#005CB2]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{info.title}</h3>
                        <p className="text-slate-700">{info.content}</p>
                        <p className="text-sm text-slate-500">{info.subContent}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200" data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle className="eta-heading-3">
                    Mesaj Gönderin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-12" data-testid="form-success">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">
                        Mesajınız Alındı!
                      </h3>
                      <p className="text-slate-600 mb-6">
                        En kısa sürede sizinle iletişime geçeceğiz.
                      </p>
                      <Button 
                        onClick={() => setSubmitted(false)}
                        className="bg-[#005CB2] hover:bg-[#004B92] text-white"
                      >
                        Yeni Mesaj Gönder
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Ad Soyad *</Label>
                          <Input
                            id="name"
                            name="name"
                            data-testid="contact-name-input"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Adınız Soyadınız"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">E-posta *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            data-testid="contact-email-input"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="ornek@email.com"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Telefon</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            data-testid="contact-phone-input"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+90 (5XX) XXX XX XX"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Konu *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            data-testid="contact-subject-input"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="Mesajınızın konusu"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Mesaj *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          data-testid="contact-message-input"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Mesajınızı buraya yazın..."
                          rows={6}
                          required
                        />
                      </div>

                      <Button 
                        type="submit" 
                        data-testid="contact-submit-btn"
                        disabled={loading}
                        className="w-full md:w-auto bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Gönderiliyor...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Mesajı Gönder
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Turkey Map Section */}
      <section className="py-16 bg-white" data-testid="turkey-map-section">
        <div className="eta-container">
          <div className="text-center mb-12">
            <p className="text-[#005CB2] font-semibold tracking-wider mb-3">HİZMET AĞIMIZ</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Türkiye Genelinde Hizmet Veriyoruz
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Türkiye'nin dört bir yanında aktif ofislerimiz ve geniş iş ağımızla yanınızdayız
            </p>
          </div>
          
          {/* Turkey Map Image */}
          <div className="relative max-w-6xl mx-auto">
            <img 
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_personnel-staffing/artifacts/z3ta803k_1000283657.png"
              alt="Eta Global Group Türkiye Hizmet Haritası"
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="bg-slate-100 pt-12" data-testid="map-section">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Google Haritalar'da Bizi Bulun
          </h2>
        </div>
        <iframe
          src="https://maps.google.com/maps?q=Kozyatağı+Mahallesi+İbrahim+Ağa+Sokak+No:12+Çelikbilek+Plaza+Kadıköy+İstanbul&t=&z=16&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Eta Global Group Konum"
        />
      </section>

    </Layout>
  );
};

export default ContactPage;
